import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { validateEstimateForm } from '../utils/formValidation';
import useCMSContent from '../hooks/useCMSContent';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SERVICES = [
  'Kitchen Remodeling',
  'Bathroom Remodeling',
  'Deck Building / Trex Decking',
  'Basement Finishing',
  'Vinyl Siding',
  'Window Replacement',
  'Custom Furniture & Aquariums',
  'Home Maintenance',
  'Other'
];

const ContactPage = () => {
  const cms = useCMSContent('contact');
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service') || '';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    service_interest: preselectedService,
    message: '',
    sms_opt_in: false,
    website: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const seoData = {
    title: 'Get Free Estimate | Visser Construction Connecticut | Home Remodeling Quote',
    description: 'Request a free home remodeling estimate from Visser General Construction in Connecticut. Kitchen, bathroom, deck, basement, siding, window, custom furniture & aquarium projects. Licensed & insured. Serving Meriden, Wallingford, Old Saybrook & CT shoreline.',
    keywords: 'free estimate home remodeling Connecticut, free quote kitchen remodel CT, bathroom renovation estimate Meriden, deck building quote Wallingford, Connecticut contractor free estimate, home improvement quote CT',
    canonical: 'https://cthomeremodels.com/contact',
    ogImage: 'https://cthomeremodels.com/og/v2/contact.png'
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setFieldErrors({});

    // Client-side validation
    const { valid, errors } = validateEstimateForm(formData);
    if (!valid) {
      setFieldErrors(errors);
      setStatus({ type: 'error', message: 'Please fix the errors below.' });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/crm/estimate`, formData, { timeout: 15000 });
      if (response.data.success) {
        setStatus({
          type: 'success',
          message: 'Thank you! We\'ll get back to you shortly with your free estimate.'
        });
        setFormData({
          name: '', email: '', phone: '', address: '', city: '',
          service_interest: '', message: '', sms_opt_in: false, website: ''
        });
        setFieldErrors({});
      } else {
        setStatus({ type: 'error', message: response.data.error || 'Please check your information and try again.' });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.response?.data?.error || error.response?.data?.detail || 'Failed to send your request. Please try again or call us directly at (203) 604-3202.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO {...seoData} />

      {/* Hero */}
      <section
        className="relative py-28 md:py-36 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(92, 7, 1, 0.85), rgba(92, 7, 1, 0.75)), url('/api/media/name/modern-kitchen-marble-countertops-quality-remodel-connecticut-hero.jpg')`
        }}
        data-testid="contact-hero"
      >
        <div className="absolute top-6 left-6 z-10">
          <img
            src="/api/media/name/visser-general-construction-llc-logo-connecticut.png"
            alt="Visser General Construction LLC Logo - Get Free Estimate"
            className="h-20 md:h-28 object-contain drop-shadow-2xl"
          />
        </div>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
            {cms('hero_title', 'Get Your Free Estimate')}
          </h1>
          <p className="text-base md:text-lg max-w-3xl mx-auto text-white drop-shadow-md">
            {cms('hero_subtitle', 'Tell us about your project and we\'ll provide a detailed, no-obligation estimate. Licensed & insured Connecticut contractor serving Meriden, Wallingford, Old Saybrook & shoreline communities.')}
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">

            {/* Left: Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold mb-6 text-[#5c0701]">Contact Information</h2>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="bg-[#3a87fe] text-white p-3 rounded-lg flex-shrink-0">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Call Us</h3>
                    <a href="tel:2036043202" className="text-[#3a87fe] hover:underline text-lg font-medium" data-testid="contact-phone">(203) 604-3202</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#3a87fe] text-white p-3 rounded-lg flex-shrink-0">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Location</h3>
                    <p className="text-gray-600">42 Gay St, Meriden, CT</p>
                    <p className="text-gray-500 text-sm mt-1">Serving homeowners within 40 miles</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#3a87fe] text-white p-3 rounded-lg flex-shrink-0">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Hours</h3>
                    <p className="text-gray-600">Mon - Fri: 5:00 AM - 5:30 PM</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#f4efe9] p-6 rounded-xl mb-6">
                <h3 className="font-bold text-[#5c0701] mb-2">Why Choose Visser Construction?</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-[#3a87fe] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <span>Licensed & insured general contractor in CT</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-[#3a87fe] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <span>Free detailed estimates, no obligation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-[#3a87fe] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <span>Quality craftsmanship on every project</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-[#3a87fe] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <span>Local Meriden team serving all of CT</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                <p className="text-sm text-gray-600">
                  <strong className="text-[#5c0701]">Serving:</strong> Meriden, Wallingford, Cheshire, Southington, Hamden, North Haven, Durham, Middletown, Berlin, Cromwell, Rocky Hill, Guilford, Madison, Clinton, Old Saybrook, Westbrook, Branford
                </p>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3 bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-2xl font-bold mb-2 text-[#5c0701]">Request Your Free Estimate</h3>
              <p className="text-gray-500 text-sm mb-6">Fill out the form below and we'll get back to you within 24 hours.</p>

              {status.message && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    status.type === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}
                  data-testid="contact-status"
                >
                  {status.message}
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form">
                {/* Honeypot — hidden from real users, bots fill it */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                  <label htmlFor="website">Website</label>
                  <input type="text" id="website" name="website" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
                </div>

                {/* Name */}
                <div className="mb-4">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-1.5 text-sm">Full Name *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required
                    placeholder="Your full name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition ${fieldErrors.name ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    data-testid="contact-name-input" />
                  {fieldErrors.name && <p className="text-red-500 text-xs mt-1">{fieldErrors.name}</p>}
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-1.5 text-sm">Email Address *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                      placeholder="your@email.com"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition ${fieldErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                      data-testid="contact-email-input" />
                    {fieldErrors.email && <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-1.5 text-sm">Phone Number *</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required
                      placeholder="(203) 555-1234"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition ${fieldErrors.phone ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                      data-testid="contact-phone-input" />
                    {fieldErrors.phone && <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>}
                  </div>
                </div>

                {/* Address */}
                <div className="mb-4">
                  <label htmlFor="address" className="block text-gray-700 font-medium mb-1.5 text-sm">Property Address</label>
                  <input type="text" id="address" name="address" value={formData.address} onChange={handleChange}
                    placeholder="123 Main St, Apt 4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition"
                    data-testid="contact-address-input" />
                </div>

                {/* City & Service */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="city" className="block text-gray-700 font-medium mb-1.5 text-sm">City / Town</label>
                    <input type="text" id="city" name="city" value={formData.city} onChange={handleChange}
                      placeholder="Meriden, CT"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition"
                      data-testid="contact-city-input" />
                  </div>
                  <div>
                    <label htmlFor="service_interest" className="block text-gray-700 font-medium mb-1.5 text-sm">Service Interested In</label>
                    <select id="service_interest" name="service_interest" value={formData.service_interest} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition bg-white"
                      data-testid="contact-service-input">
                      <option value="">Select a service</option>
                      {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className="mb-4">
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-1.5 text-sm">Project Details *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows="5"
                    placeholder="Tell us about your project — what rooms, approximate size, your vision, timeline, etc."
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent resize-none transition ${fieldErrors.message ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    data-testid="contact-message-input"></textarea>
                  {fieldErrors.message && <p className="text-red-500 text-xs mt-1">{fieldErrors.message}</p>}
                </div>

                {/* SMS Opt-in */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="sms_opt_in"
                      checked={formData.sms_opt_in}
                      onChange={handleChange}
                      className="mt-1 w-4 h-4 text-[#3a87fe] rounded border-gray-300 focus:ring-[#3a87fe]"
                      data-testid="contact-sms-optin"
                    />
                    <span className="text-sm text-gray-600">
                      I agree to receive SMS text messages from Visser General Construction LLC at the phone number provided.
                      Message and data rates may apply. Message frequency varies. Reply STOP to unsubscribe at any time. See our{' '}
                      <Link to="/terms" className="text-[#3a87fe] underline">Terms of Service</Link> and{' '}
                      <Link to="/privacy" className="text-[#3a87fe] underline">Privacy Policy</Link>.
                    </span>
                  </label>
                </div>

                {/* Submit */}
                <button type="submit" disabled={loading}
                  className={`w-full bg-[#5c0701] text-white py-4 rounded-lg font-semibold text-lg transition-colors duration-200 ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#7a1a0e]'}`}
                  data-testid="contact-submit">
                  {loading ? 'Sending Your Request...' : 'Get My Free Estimate'}
                </button>

                <p className="text-xs text-gray-400 mt-3 text-center">* Required fields. We typically respond within 24 hours.</p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
